import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';

// Friendly "X days left in your free trial" banner for members on the 7-day trial.
// Renders nothing for paid members, admins, or once dismissed for the session.
export default function TrialBanner() {
  const { user } = useAuth();
  const [endsAt, setEndsAt] = useState(user?.trial_ends_at || null);
  const [dismissed, setDismissed] = useState(false);

  const onTrial = !!user
    && !user.is_admin && !user.bootstrap_owner && !user.is_premium
    && (user.subscription_status || '').toLowerCase() === 'trialing';

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem('bp_trial_banner_dismissed') === '1'); } catch { /* ignore */ }
  }, []);

  // If the client user object doesn't carry trial_ends_at, ask the API once.
  useEffect(() => {
    if (!onTrial || endsAt) return;
    http.get('/trial/status').then(({ data }) => {
      if (data?.trial_ends_at) setEndsAt(data.trial_ends_at);
    }).catch(() => {});
  }, [onTrial, endsAt]);

  const daysLeft = useMemo(() => {
    if (!endsAt) return null;
    const end = new Date(endsAt).getTime();
    if (Number.isNaN(end)) return null;
    return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
  }, [endsAt]);

  if (!onTrial || dismissed || daysLeft === null) return null;

  const label = daysLeft <= 0
    ? 'Your free trial ends today'
    : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial`;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('bp_trial_banner_dismissed', '1'); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="trial-banner"
      className="mx-4 mt-4 lg:mx-0 flex items-center gap-3 rounded-2xl bg-navy text-white px-4 py-3 shadow-[0_8px_24px_rgba(11,25,44,0.18)]"
    >
      <Sparkles size={16} className="text-gold shrink-0" strokeWidth={2.2} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight" data-testid="trial-banner-days">{label}</p>
        <p className="text-[11px] text-white/60 leading-tight mt-0.5">Keep your Coach, journal & full library going.</p>
      </div>
      <Link
        to="/subscription/checkout"
        data-testid="trial-banner-upgrade"
        className="shrink-0 text-xs font-semibold bg-gold text-navy rounded-full px-3.5 py-2 hover:bg-gold/90 transition-smooth"
      >
        Upgrade
      </Link>
      <button
        onClick={dismiss}
        data-testid="trial-banner-dismiss"
        aria-label="Dismiss"
        className="shrink-0 text-white/50 hover:text-white transition-smooth"
      >
        <X size={16} />
      </button>
    </div>
  );
}
